import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Capacitor } from '@capacitor/core';
import { InteractiveHero } from '@/components/landing/InteractiveHero';
import { HomeTripPreview } from '@/components/landing/HomeTripPreview';
import { HomeEssentials, HomeClosingAction } from '@/components/landing/HomeEssentials';
import { HomeWhoItsFor } from '@/components/landing/HomeWhoItsFor';

import { HomeFreeVsBoost } from '@/components/landing/HomeFreeVsBoost';
import { BoostPromoBanner } from '@/components/landing/BoostPromoBanner';

import { LandingHeader } from '@/components/landing/LandingHeader';
import { LandingFooter } from '@/components/landing/LandingFooter';

const Index = () => {
  const isNativeApp = Capacitor.isNativePlatform();

  // Arriving from another page with #before-you-go (or any section hash) should land on that section.
  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    const scrollToTarget = (attempt = 0) => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempt < 10) window.setTimeout(() => scrollToTarget(attempt + 1), 100);
    };
    const timer = window.setTimeout(() => scrollToTarget(), 60);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>OdyVoy — Everything to Check Before You Go</title>
        <meta name="description" content="Packing list, visa rules, weather, health, jet lag, money and local tips for your exact trip — all in one place. Free to start." />
        <link rel="canonical" href="https://odyvoy.com/" />
        <meta property="og:title" content="OdyVoy — Everything to Check Before You Go" />
        <meta property="og:description" content="Packing list, visa rules, weather, health, jet lag, money and local tips for your exact trip — in one place. Free to start." />
        <meta property="og:url" content="https://odyvoy.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://odyvoy.com/lovable-uploads/odyvoy-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OdyVoy — Everything to Check Before You Go" />
        <meta name="twitter:description" content="Packing list, visa rules, weather, health, jet lag, money and local tips for your exact trip — in one place. Free to start." />
        <meta name="twitter:image" content="https://odyvoy.com/lovable-uploads/odyvoy-logo.png" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "OdyVoy",
            "applicationCategory": "TravelApplication",
            "description": "AI travel planner that checks your packing list, visa requirements, weather, health precautions, budget and local tips for your exact destination and dates.",
            "operatingSystem": "iOS, Android, Web",
            "url": "https://odyvoy.com/",
            "award": "AI-powered Travel Planner of the Year — Greece",
            "featureList": [
              "AI packing list tailored to destination, weather and baggage limits",
              "Visa and entry requirement checker with official links",
              "Seasonal weather forecast for your travel dates",
              "Health, vaccination and jet lag guidance",
              "Currency converter, photo scanner and group expense splitting",
              "Local etiquette, transport and safety tips",
              "eSIM comparison for staying connected",
              "Shared packing lists for travel companions",
              "Pre-trip reminders"
            ],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "EUR"
            }
          })}
        </script>
      </Helmet>

      <div className="home-editorial min-h-screen bg-background">
        <LandingHeader />

        <main>
          <InteractiveHero />

          {!isNativeApp && (
            <>
              <HomeTripPreview />
              <HomeEssentials />
              <HomeWhoItsFor />
              <HomeFreeVsBoost />
            </>
          )}
          <BoostPromoBanner />
          <HomeClosingAction />
        </main>

        <LandingFooter />

      </div>
    </>
  );
};

export default Index;
